/**
 * WaterQualityPredictorPage.tsx — Headline water-quality forecast page
 *
 * Synthesises the full ecosystem response under adjustable aeration into a
 * single forward-looking chart. Lets the user move every relevant control
 * (module count, airflow, blower kW, alpha, days running, water T) and see
 * the predicted trajectory of DO, BOD, NH4, NO3, P, chl-a, cyanobacteria,
 * turbidity and the composite Water Quality Index over 180 days.
 *
 * Same kinetic core as Algal Dynamics so numbers stay consistent.
 *
 * References (all already cited elsewhere): Henze 2000, Pesce & Wunderlin 2000,
 * Brown & Barnwell 1987 (QUAL2E), Chapra 1997, Reynolds 2006, Visser 1996,
 * Wüest 1992, Stenstrom 2008.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Activity, ArrowLeft, Power, Sliders, RefreshCcw, Info,
  CheckCircle2, AlertTriangle,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import {
  specFromLegacy, type LegacyModSpec,
  bubbleDiameterMm, sotePerMetre, qPerModuleNm3h,
} from '@/lib/diffuserPhysics';

const G = 9.81;
const RESERVOIR_AREA_M2 = 34_000;

interface ModSpec extends LegacyModSpec {
  waterDepthM: number; mountingHeightM: number; waterTempC: number;
}
const MOD_DEFAULTS: ModSpec = {
  diffuserCount: 8, diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300,
  poreSizeUm: 80, airflowPerDiffNm3h: 1.2,
  waterDepthM: 3.0, mountingHeightM: 0.40, waterTempC: 12,
};

interface ControlState {
  aerationOn: boolean;
  moduleCount: number;
  airflowOverride: number;
  useSession: boolean;
  alpha: number;
  startDay: number;
  trialDays: number;
  T_water: number;
  // Inflow forcings
  inflow_BOD: number;
  inflow_NH4: number;
  inflow_P: number;
  initial_DO: number;
}
const DEFAULT_CONTROL: ControlState = {
  aerationOn: true, moduleCount: 20, airflowOverride: 1.2, useSession: true,
  alpha: 0.85, startDay: 30, trialDays: 180, T_water: 17,
  inflow_BOD: 6, inflow_NH4: 0.4, inflow_P: 0.08, initial_DO: 3.0,
};

const KEY = 'rb_wq_predictor_v1';

function fmt(n: number, d = 2) { if (!isFinite(n)) return '—'; return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }); }
function doSat(T: number) { return 14.652 - 0.41022 * T + 0.0079910 * T * T - 0.000077774 * T * T * T; }

// Henze / Monod constants
const H = { mu_max: 6.0, K_S: 5.0, K_O: 0.20, Y: 0.67, b: 0.62, theta: 1.07 };
const A = { mu_max: 0.8, K_N: 1.0, K_O: 0.5, Y: 0.24, b: 0.15, theta: 1.10 };

interface Trajectory {
  day: number; aerating: boolean;
  DO: number; BOD: number;
  NH4: number; NO3: number; P: number;
  chl_a: number; cyano: number;
  bact: number; nitri: number;
  turb: number; WQI: number;
}

function wqi(s: { DO: number; BOD: number; NH4: number; chl_a: number; cyano: number; turb: number }): number {
  const I_DO = Math.min(100, (s.DO / 9) * 100);
  const I_BOD = Math.max(0, 100 - s.BOD * 12);
  const I_NH4 = Math.max(0, 100 - s.NH4 * 100);
  const I_chl = Math.max(0, 100 - s.chl_a * 1.8);
  const I_turb = Math.max(0, 100 - s.turb * 4);
  const I_cyano = Math.max(0, 100 - s.cyano * 3.5);
  return I_DO * 0.25 + I_BOD * 0.20 + I_NH4 * 0.15 + I_chl * 0.15 + I_turb * 0.10 + I_cyano * 0.15;
}

function aerationAOTR_kgH(spec: ModSpec, ctl: ControlState, DO: number): number {
  if (!ctl.aerationOn || ctl.moduleCount === 0) return 0;
  const q = ctl.useSession ? spec.airflowPerDiffNm3h : ctl.airflowOverride;
  const T = ctl.T_water;
  const d_b = bubbleDiameterMm(specFromLegacy({ ...spec, airflowPerDiffNm3h: q }), T);
  const sote_m = sotePerMetre(d_b);
  const H_eff = Math.max(0.3, spec.waterDepthM - spec.mountingHeightM);
  const SOTR_mod = (q * spec.diffuserCount) * 1.293 * 0.2314 * (sote_m * H_eff / 100);
  const AOTR_mod = SOTR_mod * ctl.alpha * 0.98 * Math.pow(1.024, T - 20) * Math.max(0, doSat(T) - DO) / 9.09;
  return AOTR_mod * ctl.moduleCount;
}

function runTrajectory(spec: ModSpec, ctl: ControlState): Trajectory[] {
  const out: Trajectory[] = [];
  // initial state
  let DO = ctl.initial_DO;
  let BOD = ctl.inflow_BOD;
  let NH4 = ctl.inflow_NH4;
  let NO3 = 1.1;
  let P = ctl.inflow_P;
  let chl_a = 18, cyano = 9;
  let bact = 2.0, nitri = 0.4;
  let turb = 7;
  const V_res = RESERVOIR_AREA_M2 * spec.waterDepthM;
  const T = ctl.T_water;
  const reaerK = 0.5;
  const SOD_mgLd = 0.6;
  for (let d = 0; d <= ctl.trialDays; d++) {
    const on = ctl.aerationOn && d >= ctl.startDay;
    const q_total = ctl.moduleCount * spec.diffuserCount * (ctl.useSession ? spec.airflowPerDiffNm3h : ctl.airflowOverride);
    const eps = on ? (q_total / 3600 * G * spec.waterDepthM) / V_res : 0;
    const AOTR = aerationAOTR_kgH(spec, { ...ctl, aerationOn: on }, DO);
    const DO_aer = (AOTR * 24 * 1e6) / V_res / 1000;
    const DO_re = reaerK * Math.max(0, doSat(T) - DO);
    // Heterotrophs
    const mu_h = H.mu_max * Math.pow(H.theta, T - 20) * (BOD / (H.K_S + BOD)) * (DO / (H.K_O + DO)) - H.b * Math.pow(H.theta, T - 20);
    const bact_new = Math.max(0.1, Math.min(50, bact + mu_h * bact));
    const BOD_used = (mu_h + H.b * Math.pow(H.theta, T - 20)) * bact / H.Y;
    // Nitrifiers
    const mu_a = A.mu_max * Math.pow(A.theta, T - 20) * (NH4 / (A.K_N + NH4)) * (DO / (A.K_O + DO)) - A.b * Math.pow(A.theta, T - 20);
    const nitri_new = Math.max(0.05, Math.min(10, nitri + mu_a * nitri));
    const NH4_ox = mu_a * nitri / A.Y;
    // Algae — simplified lumped + cyano subcompartment
    const algGrowthFactor = (P / (0.005 + P)) * Math.exp(-Math.pow((T - 20) / 10, 2)) * Math.max(0, 1 - turb / 30);
    const algGrowth = on
      ? algGrowthFactor * 0.05 * chl_a * (1 - chl_a / 60)
      : algGrowthFactor * 0.15 * chl_a * (1 - chl_a / 80);
    chl_a = Math.max(1, Math.min(80, chl_a + algGrowth - 0.05 * chl_a));
    const cyanoSuppressed = eps > 5e-7;
    const cyanoGrowth = (cyanoSuppressed ? 0.02 : 0.12) * cyano * (1 - cyano / (cyanoSuppressed ? 12 : 50));
    cyano = Math.max(0.5, Math.min(50, cyano + cyanoGrowth - 0.05 * cyano));
    // P dynamics
    const P_release = on ? 0.001 * 0.15 : 0.001;
    P = Math.max(0.001, P + P_release - 0.0001 * chl_a);
    // DO balance
    const O2_bact = mu_h * bact * 1.4 / H.Y;
    const O2_nitri = 4.57 * NH4_ox;
    const DO_change = DO_aer + DO_re - SOD_mgLd - O2_bact - O2_nitri;
    DO = Math.max(0, Math.min(doSat(T), DO + DO_change));
    BOD = Math.max(0.5, BOD + (on ? 0.1 : 0.2) - BOD_used);
    NH4 = Math.max(0, NH4 + 0.05 - NH4_ox);
    NO3 = Math.max(0, NO3 + NH4_ox * 0.95 - 0.01 * NO3);
    turb = Math.max(2, turb + 0.05 * (chl_a + cyano - 25) * 0.01);
    bact = bact_new; nitri = nitri_new;
    const score = wqi({ DO, BOD, NH4, chl_a, cyano, turb });
    out.push({ day: d, aerating: on, DO, BOD, NH4, NO3, P, chl_a, cyano, bact, nitri, turb, WQI: score });
  }
  return out;
}

const PARAMS: { key: keyof Trajectory; label: string; unit: string; colour: string; yMin: number; yMax: number; thickness?: number }[] = [
  { key: 'WQI',    label: 'Water Quality Index', unit: '/100',    colour: '#a78bfa', yMin: 0,  yMax: 100, thickness: 2.5 },
  { key: 'DO',     label: 'Dissolved O₂',        unit: 'mg/L',    colour: '#38bdf8', yMin: 0,  yMax: 14,  thickness: 2.5 },
  { key: 'BOD',    label: 'BOD',                 unit: 'mg/L',    colour: '#fb923c', yMin: 0,  yMax: 20 },
  { key: 'NH4',    label: 'Ammonia',             unit: 'mg/L',    colour: '#dc2626', yMin: 0,  yMax: 3 },
  { key: 'NO3',    label: 'Nitrate',             unit: 'mg/L',    colour: '#0ea5e9', yMin: 0,  yMax: 8 },
  { key: 'P',      label: 'Phosphorus',          unit: 'mg/L',    colour: '#d946ef', yMin: 0,  yMax: 0.3 },
  { key: 'chl_a',  label: 'Chlorophyll-a',       unit: 'µg/L',    colour: '#22c55e', yMin: 0,  yMax: 80 },
  { key: 'cyano',  label: 'Cyanobacteria',       unit: 'µg/L',    colour: '#ef4444', yMin: 0,  yMax: 50 },
  { key: 'bact',   label: 'Heterotrophs',        unit: 'mg/L VSS',colour: '#f59e0b', yMin: 0,  yMax: 30 },
  { key: 'nitri',  label: 'Nitrifiers',          unit: 'mg/L VSS',colour: '#06b6d4', yMin: 0,  yMax: 8 },
  { key: 'turb',   label: 'Turbidity',           unit: 'NTU',     colour: '#7c3aed', yMin: 0,  yMax: 30 },
];

export default function WaterQualityPredictorPage() {
  const navigate = useNavigate();
  const [ctl, setCtl] = useState<ControlState>(() => loadPersisted<ControlState>(KEY, DEFAULT_CONTROL));
  const [spec, setSpec] = useState<ModSpec>(() => ({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) }));
  const [selected, setSelected] = useState<string[]>(['WQI', 'DO', 'chl_a', 'bact']);
  // Live array-config module count for sync hint
  const [arrayModuleCount, setArrayModuleCount] = useState<number | null>(() => {
    try {
      const raw = localStorage.getItem('rb_module_array_v1');
      if (!raw) return null;
      return (JSON.parse(raw) as { moduleCount?: number }).moduleCount ?? null;
    } catch { return null; }
  });

  useEffect(() => { savePersisted(KEY, ctl); }, [ctl]);
  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === KEYS.DIFFUSER_MODULE) setSpec({ ...MOD_DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, MOD_DEFAULTS) });
      if (k === 'rb_module_array_v1') {
        try {
          const raw = localStorage.getItem('rb_module_array_v1');
          if (raw) setArrayModuleCount((JSON.parse(raw) as { moduleCount?: number }).moduleCount ?? null);
        } catch { /* ignore */ }
      }
    });
    return () => unsub();
  }, []);

  // ON and OFF trajectories side-by-side
  const trajOn = useMemo(() => runTrajectory(spec, { ...ctl, aerationOn: true }), [spec, ctl]);
  const trajOff = useMemo(() => runTrajectory(spec, { ...ctl, aerationOn: false, moduleCount: 0 }), [spec, ctl]);

  const last_on = trajOn[trajOn.length - 1];
  const last_off = trajOff[trajOff.length - 1];

  const grade = (s: number) => s >= 85 ? 'Excellent' : s >= 70 ? 'Good' : s >= 50 ? 'Acceptable' : s >= 30 ? 'Poor' : 'Very poor';
  const gradeColour = (s: number) => s >= 85 ? 'text-emerald-300' : s >= 70 ? 'text-sky-300' : s >= 50 ? 'text-amber-300' : s >= 30 ? 'text-orange-300' : 'text-red-300';

  // Aeration ε
  const qPerMod = useMemo(() => qPerModuleNm3h(specFromLegacy({
    ...spec, airflowPerDiffNm3h: ctl.useSession ? spec.airflowPerDiffNm3h : ctl.airflowOverride,
  })), [spec, ctl]);
  const Q_m3s = (ctl.moduleCount * qPerMod) / 3600;
  const V_res = RESERVOIR_AREA_M2 * spec.waterDepthM;
  const eps = ctl.aerationOn ? (Q_m3s * G * spec.waterDepthM) / V_res : 0;

  // Chart geometry
  const svgW = 720, svgH = 360;
  const padL = 50, padR = 50, padT = 25, padB = 30;
  const plotW = svgW - padL - padR;
  const plotH = svgH - padT - padB;
  const x = (d: number) => padL + (d / ctl.trialDays) * plotW;
  const yPosForParam = (p: typeof PARAMS[number], v: number) =>
    padT + plotH - ((v - p.yMin) / Math.max(0.001, p.yMax - p.yMin)) * plotH;

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Activity}
        title="Water Quality Predictor — Live Aeration Scenario"
        subtitle="Adjustable aeration, 180-day forward trajectory, with vs without aeration side-by-side"
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      {/* Aeration control panel */}
      <div className="rounded-2xl border-2 border-sky-700 bg-gradient-to-br from-sky-900/30 to-slate-900 p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sliders className="w-5 h-5 text-sky-300" />
            <div className="text-sm font-bold text-sky-200">Aeration scenario</div>
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${
              ctl.aerationOn ? 'bg-emerald-900/40 text-emerald-300' : 'bg-slate-800 text-slate-400'
            }`}>
              <Power className="w-3 h-3" />
              {ctl.aerationOn ? 'ON' : 'OFF'}
            </span>
          </div>
          <button onClick={() => setCtl(c => ({ ...c, aerationOn: !c.aerationOn }))}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${
              ctl.aerationOn ? 'border-red-700 bg-red-900/30 text-red-200' : 'border-emerald-700 bg-emerald-900/30 text-emerald-200'
            }`}>
            <Power className="w-3.5 h-3.5" />
            {ctl.aerationOn ? 'Switch OFF' : 'Switch ON'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
          <div>
            <label className="text-slate-400 block mb-1 flex items-center justify-between">
              <span>Modules: <strong className="text-slate-100">{ctl.moduleCount}</strong></span>
              {arrayModuleCount !== null && arrayModuleCount !== ctl.moduleCount && (
                <button onClick={() => setCtl(c => ({ ...c, moduleCount: arrayModuleCount }))}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-sky-900/40 text-sky-300 hover:bg-sky-900/60">
                  ↺ sync ({arrayModuleCount})
                </button>
              )}
            </label>
            <input type="range" min={0} max={60} step={1} value={ctl.moduleCount}
              onChange={e => setCtl(c => ({ ...c, moduleCount: Number(e.target.value) }))}
              className="w-full accent-sky-500" />
          </div>
          <div>
            <label className="text-slate-400 block mb-1">Airflow per diff: <strong className="text-slate-100">{fmt(ctl.useSession ? spec.airflowPerDiffNm3h : ctl.airflowOverride, 2)} Nm³/h</strong></label>
            <input type="range" min={0.3} max={3.0} step={0.05} value={ctl.airflowOverride} disabled={ctl.useSession}
              onChange={e => setCtl(c => ({ ...c, airflowOverride: Number(e.target.value) }))}
              className={`w-full ${ctl.useSession ? 'opacity-50' : 'accent-sky-500'}`} />
            <label className="flex items-center gap-1 text-[10px] mt-0.5">
              <input type="checkbox" checked={ctl.useSession}
                onChange={e => setCtl(c => ({ ...c, useSession: e.target.checked }))}
                className="accent-sky-500" />
              use session value
            </label>
          </div>
          <div>
            <label className="text-slate-400 block mb-1">α-factor: <strong className="text-slate-100">{fmt(ctl.alpha, 2)}</strong></label>
            <input type="range" min={0.4} max={1.0} step={0.05} value={ctl.alpha}
              onChange={e => setCtl(c => ({ ...c, alpha: Number(e.target.value) }))}
              className="w-full accent-sky-500" />
          </div>
          <div>
            <label className="text-slate-400 block mb-1">Aeration starts: day <strong className="text-slate-100">{ctl.startDay}</strong></label>
            <input type="range" min={0} max={Math.min(120, ctl.trialDays - 10)} step={1} value={ctl.startDay}
              onChange={e => setCtl(c => ({ ...c, startDay: Number(e.target.value) }))}
              className="w-full accent-amber-500" />
          </div>
          <div>
            <label className="text-slate-400 block mb-1">Horizon: <strong className="text-slate-100">{ctl.trialDays} d</strong></label>
            <input type="range" min={30} max={365} step={5} value={ctl.trialDays}
              onChange={e => setCtl(c => ({ ...c, trialDays: Number(e.target.value) }))}
              className="w-full accent-sky-500" />
          </div>
          <div>
            <label className="text-slate-400 block mb-1">Water T: <strong className="text-slate-100">{fmt(ctl.T_water, 1)} °C</strong></label>
            <input type="range" min={4} max={26} step={0.5} value={ctl.T_water}
              onChange={e => setCtl(c => ({ ...c, T_water: Number(e.target.value) }))}
              className="w-full accent-sky-500" />
          </div>
          <div>
            <label className="text-slate-400 block mb-1">Inflow BOD: <strong className="text-slate-100">{fmt(ctl.inflow_BOD, 1)}</strong></label>
            <input type="range" min={1} max={20} step={0.5} value={ctl.inflow_BOD}
              onChange={e => setCtl(c => ({ ...c, inflow_BOD: Number(e.target.value) }))}
              className="w-full accent-orange-500" />
          </div>
          <div>
            <label className="text-slate-400 block mb-1">Initial DO: <strong className="text-slate-100">{fmt(ctl.initial_DO, 1)}</strong></label>
            <input type="range" min={0} max={10} step={0.1} value={ctl.initial_DO}
              onChange={e => setCtl(c => ({ ...c, initial_DO: Number(e.target.value) }))}
              className="w-full accent-sky-500" />
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="rounded-lg bg-slate-900/50 border border-slate-700 px-3 py-2">
            <div className="text-slate-400">Total airflow</div>
            <div className="text-slate-100 font-bold">{fmt(ctl.moduleCount * qPerMod, 0)} Nm³/h</div>
          </div>
          <div className="rounded-lg bg-slate-900/50 border border-slate-700 px-3 py-2">
            <div className="text-slate-400">ε aeration</div>
            <div className="text-slate-100 font-bold">{eps.toExponential(2)} W/kg</div>
          </div>
          <div className="rounded-lg bg-slate-900/50 border border-slate-700 px-3 py-2">
            <div className="text-slate-400">Cyano suppression</div>
            <div className={`font-bold ${eps > 5e-7 ? 'text-emerald-300' : 'text-red-300'}`}>
              {eps > 5e-7 ? 'YES' : 'NO'}
            </div>
          </div>
          <button onClick={() => setCtl(DEFAULT_CONTROL)}
            className="rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-slate-300 hover:bg-slate-700">
            <RefreshCcw className="w-3.5 h-3.5 inline mr-1" /> Reset
          </button>
        </div>
      </div>

      {/* Headline outcomes — with vs without */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl border-2 border-emerald-700 bg-emerald-900/15 p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-300" />
            <div className="text-sm font-bold text-emerald-200">With aeration ({ctl.moduleCount} modules)</div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div><div className="text-slate-400">WQI</div><div className={`text-2xl font-bold ${gradeColour(last_on.WQI)}`}>{fmt(last_on.WQI, 0)}</div><div className="text-[10px] text-slate-500">{grade(last_on.WQI)}</div></div>
            <div><div className="text-slate-400">DO</div><div className="text-2xl font-bold text-sky-200">{fmt(last_on.DO, 1)}</div><div className="text-[10px] text-slate-500">mg/L</div></div>
            <div><div className="text-slate-400">Chl-a</div><div className="text-2xl font-bold text-emerald-200">{fmt(last_on.chl_a, 0)}</div><div className="text-[10px] text-slate-500">µg/L</div></div>
            <div><div className="text-slate-400">Cyano</div><div className="text-xl font-bold text-red-300">{fmt(last_on.cyano, 0)}</div></div>
            <div><div className="text-slate-400">BOD</div><div className="text-xl font-bold text-amber-300">{fmt(last_on.BOD, 1)}</div></div>
            <div><div className="text-slate-400">NH₄</div><div className="text-xl font-bold text-red-300">{fmt(last_on.NH4, 2)}</div></div>
          </div>
        </div>
        <div className="rounded-xl border-2 border-red-700 bg-red-900/10 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-300" />
            <div className="text-sm font-bold text-red-200">Without aeration (do-nothing)</div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div><div className="text-slate-400">WQI</div><div className={`text-2xl font-bold ${gradeColour(last_off.WQI)}`}>{fmt(last_off.WQI, 0)}</div><div className="text-[10px] text-slate-500">{grade(last_off.WQI)}</div></div>
            <div><div className="text-slate-400">DO</div><div className="text-2xl font-bold text-sky-200">{fmt(last_off.DO, 1)}</div><div className="text-[10px] text-slate-500">mg/L</div></div>
            <div><div className="text-slate-400">Chl-a</div><div className="text-2xl font-bold text-emerald-200">{fmt(last_off.chl_a, 0)}</div><div className="text-[10px] text-slate-500">µg/L</div></div>
            <div><div className="text-slate-400">Cyano</div><div className="text-xl font-bold text-red-300">{fmt(last_off.cyano, 0)}</div></div>
            <div><div className="text-slate-400">BOD</div><div className="text-xl font-bold text-amber-300">{fmt(last_off.BOD, 1)}</div></div>
            <div><div className="text-slate-400">NH₄</div><div className="text-xl font-bold text-red-300">{fmt(last_off.NH4, 2)}</div></div>
          </div>
        </div>
      </div>

      {/* Delta callout */}
      <div className={`rounded-xl border-2 ${last_on.WQI > last_off.WQI ? 'border-emerald-700 bg-emerald-900/15' : 'border-amber-700 bg-amber-900/15'} p-4`}>
        <div className="text-sm font-bold text-slate-100">
          Aeration effect:&nbsp;
          <span className={`text-2xl font-bold ${last_on.WQI > last_off.WQI ? 'text-emerald-300' : 'text-amber-300'}`}>
            {last_on.WQI > last_off.WQI ? '+' : ''}{fmt(last_on.WQI - last_off.WQI, 1)}
          </span>&nbsp;
          <span className="text-slate-400">WQI points at day {ctl.trialDays}</span>
        </div>
        <div className="text-xs text-slate-400 mt-1">
          ΔDO {fmt(last_on.DO - last_off.DO, 2)} mg/L · ΔChl-a {fmt(last_on.chl_a - last_off.chl_a, 1)} µg/L ·
          ΔCyano {fmt(last_on.cyano - last_off.cyano, 1)} µg/L · ΔBOD {fmt(last_on.BOD - last_off.BOD, 1)} mg/L ·
          ΔNH₄ {fmt(last_on.NH4 - last_off.NH4, 2)} mg/L
        </div>
      </div>

      {/* Param chooser */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-3">
        <div className="flex flex-wrap gap-1.5">
          {PARAMS.map(p => (
            <button key={p.key} onClick={() => setSelected(s => s.includes(p.key) ? s.filter(k => k !== p.key) : [...s, p.key])}
              className={`text-[11px] px-2.5 py-1 rounded border transition ${
                selected.includes(p.key) ? 'text-slate-100' : 'border-slate-700 bg-slate-800 text-slate-500 hover:text-slate-300'
              }`}
              style={selected.includes(p.key) ? { borderColor: p.colour, color: p.colour, background: `${p.colour}15` } : {}}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Side-by-side dual trajectory chart */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">{ctl.trialDays}-day trajectory — solid = aeration ON · dashed = OFF (comparison)</div>
        <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto bg-slate-950 rounded">
          {/* Axes */}
          <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
          <line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
          {/* X labels */}
          {Array.from({ length: 7 }, (_, i) => Math.round((i / 6) * ctl.trialDays)).map(d => (
            <text key={d} x={x(d)} y={padT + plotH + 15} textAnchor="middle" fontSize={9} fill="#94a3b8">d{d}</text>
          ))}
          {/* Aeration zone shading */}
          <rect x={padL} y={padT} width={x(ctl.startDay) - padL} height={plotH} fill="rgba(239, 68, 68, 0.04)" />
          <rect x={x(ctl.startDay)} y={padT} width={padL + plotW - x(ctl.startDay)} height={plotH} fill="rgba(34, 197, 94, 0.04)" />
          <line x1={x(ctl.startDay)} y1={padT} x2={x(ctl.startDay)} y2={padT + plotH} stroke="#fbbf24" strokeWidth={1} strokeDasharray="3 3" />
          <text x={x(ctl.startDay) + 2} y={padT + 10} fontSize={9} fill="#fbbf24">aeration starts →</text>
          {/* Plot each selected param: solid for ON, dashed for OFF */}
          {PARAMS.filter(p => selected.includes(p.key)).map(p => {
            const ptsOn = trajOn.map(d => `${x(d.day)},${yPosForParam(p, d[p.key] as number)}`).join(' ');
            const ptsOff = trajOff.map(d => `${x(d.day)},${yPosForParam(p, d[p.key] as number)}`).join(' ');
            return (
              <g key={p.key}>
                <polyline fill="none" stroke={p.colour} strokeWidth={p.thickness ?? 1.5} points={ptsOn} opacity={0.95} />
                <polyline fill="none" stroke={p.colour} strokeWidth={(p.thickness ?? 1.5) * 0.7} points={ptsOff}
                  strokeDasharray="4 3" opacity={0.55} />
              </g>
            );
          })}
          {/* Y axis labels for first two selected */}
          {selected.slice(0, 2).map((k, idx) => {
            const p = PARAMS.find(pp => pp.key === k); if (!p) return null;
            const xOff = idx === 0 ? padL - 4 : padL + plotW + 4;
            const align: 'end' | 'start' = idx === 0 ? 'end' : 'start';
            return (
              <g key={k}>
                {[0, 0.25, 0.5, 0.75, 1.0].map(f => {
                  const v = p.yMin + f * (p.yMax - p.yMin);
                  return (
                    <text key={f} x={xOff} y={padT + plotH - f * plotH + 3} textAnchor={align}
                      fontSize={8} fill={p.colour}>
                      {fmt(v, p.yMax > 50 ? 0 : 1)}
                    </text>
                  );
                })}
              </g>
            );
          })}
        </svg>
        <div className="text-[10px] text-slate-500 mt-1 flex flex-wrap gap-3">
          <span>Solid = aeration ON scenario · Dashed = OFF (comparison)</span>
          <span>First selected param scales left axis (colour-matched), second scales right axis</span>
        </div>
      </div>

      {/* Methodology */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> How the predictor works
        </div>
        <div className="text-xs text-slate-400 leading-relaxed">
          The page runs a daily Euler simulation of an 11-variable coupled ecosystem model: dissolved oxygen,
          BOD, ammonia, nitrate, phosphorus, total chl-a, cyanobacteria chl-a, heterotrophic bacteria,
          autotrophic nitrifiers, turbidity, and the composite Water Quality Index. The aeration plant
          delivers AOTR computed live from the active diffuser spec (Stenstrom 2008 SOTE, α-β-T corrected),
          which directly raises DO. Higher DO unlocks the aerobic-bacteria Monod term (Henze 2000 ASM1) —
          heterotrophs draw down BOD, autotrophic nitrifiers oxidise NH₄ to NO₃. Stronger mixing (Wüest 1992 ε)
          above the Visser 1996 critical threshold disadvantages cyanobacteria buoyancy. Lower internal P
          loading (Welch & Cooke 1995) slows algae overall. The composite WQI (Pesce & Wunderlin 2000)
          weighs every output into a single client-friendly figure.
        </div>
      </div>
    </div>
  );
}
